/** Bloc Détails — SIRENE puis identité légale et contact. */
import { useEffect, useState } from "react";
import { facturxFetch } from "../../api/client";
import { CopyButton } from "../../components/CopyButton";
import { Field } from "../../components/Field";
import { useCompany } from "../../company/CompanyProvider";
import { legalFormIsSociete } from "../../legal/mentions";
import { compactBic, compactIban, formatIban } from "../../payments/iban";
import { SiretSearch, type SiretActivity, type SiretPhase } from "../clients/SiretSearch";
import type { CompanyDraft } from "./types";
import type { SireneStatus } from "../clients/types";

type Match = Partial<{
  name: string;
  siret: string;
  siren: string;
  vat_number: string;
  sector: string;
  address_line: string;
  address_line2: string;
  postal_code: string;
  city: string;
  region: string;
  country_code: string;
  sirene_status: SireneStatus;
  sirene_closed_on: string | null;
  sirene_created_on: string | null;
}>;

type CompanyDetailsProps = {
  companyId: number;
  draft: CompanyDraft;
  setDraft: (next: CompanyDraft) => void;
  busy: boolean;
  setBusy: (v: boolean) => void;
  onError: (msg: string) => void;
};

export function CompanyDetails({ companyId, draft, setDraft, busy, setBusy, onError }: CompanyDetailsProps) {
  const { company } = useCompany();
  const franchise = (company?.vat_regime || "") === "franchise";
  const societe = legalFormIsSociete(draft.legal_form);
  const [phase, setPhase] = useState<SiretPhase>("idle");
  const [foundName, setFoundName] = useState("");
  const [lookupErr, setLookupErr] = useState("");
  const [query, setQuery] = useState(draft.siret);
  const [activity, setActivity] = useState<SiretActivity | null>(null);

  useEffect(() => {
    setQuery(draft.siret);
    setPhase("idle");
    setFoundName("");
    setLookupErr("");
    setActivity(null);
  }, [companyId]);

  function set<K extends keyof CompanyDraft>(key: K, value: CompanyDraft[K]) {
    setDraft({ ...draft, [key]: value });
  }

  async function lookup() {
    const q = query.trim() || draft.siret.trim() || draft.legal_name.trim();
    if (!q) {
      setPhase("err");
      setLookupErr("Indique un SIRET ou un nom");
      return;
    }
    setBusy(true);
    setPhase("scan");
    setLookupErr("");
    onError("");
    try {
      const data = await facturxFetch<{ match: Match }>(`siret-lookup.php?q=${encodeURIComponent(q)}`);
      const m = data.match;
      const next: CompanyDraft = {
        ...draft,
        legal_name: m.name || draft.legal_name,
        siret: String(m.siret || draft.siret),
        siren: String(m.siren || draft.siren),
        vat_number: m.vat_number || draft.vat_number,
        sector: m.sector || draft.sector,
        address_line: m.address_line || draft.address_line,
        address_line2: m.address_line2 || draft.address_line2,
        postal_code: m.postal_code || draft.postal_code,
        city: m.city || draft.city,
        region: m.region || draft.region,
        country_code: m.country_code || draft.country_code,
      };
      setDraft(next);
      setQuery(next.siret);
      setFoundName(next.legal_name);
      setActivity({
        sirene_status: m.sirene_status,
        sirene_closed_on: m.sirene_closed_on,
        sirene_created_on: m.sirene_created_on,
      });
      setPhase("ok");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Recherche SIRET impossible";
      setPhase("err");
      setLookupErr(msg);
      onError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-details">
      <SiretSearch
        value={query}
        onChange={(v) => {
          setQuery(v);
          const digits = v.replace(/\D/g, "");
          if (digits.length === 14) set("siret", digits);
          else if (phase !== "scan") setPhase("idle");
          setActivity(null);
        }}
        phase={phase}
        foundName={foundName}
        error={lookupErr}
        disabled={busy}
        activity={activity}
        onSearch={() => void lookup()}
      />
      {phase === "ok" ? (
        <p className="fx-co-form__hint">Adresse aussi posée dans l’onglet Adresse — relis puis Enregistrer.</p>
      ) : null}
      <div className="fx-co-grid">
        <Field label="Nom de l’entreprise" value={draft.legal_name} onChange={(e) => set("legal_name", e.target.value)} />
        <Field
          label="SIRET"
          value={draft.siret}
          onChange={(e) => set("siret", e.target.value)}
          inputMode="numeric"
          suffix={
            draft.siret.replace(/\D/g, "").length >= 9 ? (
              <CopyButton text={draft.siret.replace(/\D/g, "")} label="Copier le SIRET" />
            ) : null
          }
        />
        <Field
          label="SIREN"
          value={draft.siren}
          inputMode="numeric"
          hint="9 chiffres — champ distinct du SIRET"
          onChange={(e) => set("siren", e.target.value.replace(/\D/g, "").slice(0, 9))}
          suffix={
            draft.siren.replace(/\D/g, "").length === 9 ? (
              <CopyButton text={draft.siren.replace(/\D/g, "")} label="Copier le SIREN" />
            ) : null
          }
        />
        <Field
          label="Numéro de TVA"
          value={draft.vat_number}
          disabled={franchise}
          hint={franchise ? "Grisé — régime micro / franchise en base (Paramètres → Taxes)" : undefined}
          onChange={(e) => set("vat_number", e.target.value)}
        />
        <Field
          label="Forme juridique"
          value={draft.legal_form}
          onChange={(e) => set("legal_form", e.target.value)}
          hint="SAS, SARL, EI… obligatoire pour envoyer. Capital + RCS si société."
        />
        <Field
          label="Capital social"
          value={draft.share_capital}
          disabled={!societe}
          onChange={(e) => set("share_capital", e.target.value)}
          hint={societe ? "Ex. 10 000 €" : "Grisé — EI / micro"}
        />
        <Field
          label="RCS / RM"
          value={draft.rcs_city}
          disabled={!societe}
          onChange={(e) => set("rcs_city", e.target.value)}
          hint={societe ? "Ville du greffe, ex. Rouen" : "Grisé — EI / micro"}
        />
        <Field label="Site web" value={draft.website} onChange={(e) => set("website", e.target.value)} inputMode="url" />
        <Field label="E-mail" value={draft.email} onChange={(e) => set("email", e.target.value)} type="email" />
        <Field label="Téléphone" value={draft.phone} onChange={(e) => set("phone", e.target.value)} type="tel" />
        <Field
          label="IBAN"
          value={draft.iban}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => set("iban", formatIban(e.target.value))}
          suffix={compactIban(draft.iban).length >= 15 ? <CopyButton text={compactIban(draft.iban)} label="Copier l’IBAN" /> : null}
        />
        <Field
          label="BIC"
          value={draft.bic}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => set("bic", compactBic(e.target.value))}
          suffix={draft.bic.length >= 8 ? <CopyButton text={draft.bic} label="Copier le BIC" /> : null}
        />
        <Field
          label="Secteur"
          value={draft.sector}
          onChange={(e) => set("sector", e.target.value)}
          hint="Code NAF / APE ou activité"
        />
      </div>
    </div>
  );
}
